import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber, maskNumber } from '../../../utils/formatUtils';

const FuelCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [alc, setAlc] = useState('');
  const [gas, setGas] = useState('');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const alcVal = parseFloat(parseBrazilianNumber(alc)) || 0;
    const gasVal = parseFloat(parseBrazilianNumber(gas)) || 0;

    if (alcVal <= 0 || gasVal <= 0) return;

    const ratio = alcVal / gasVal;
    const recommend = ratio <= 0.7 ? 'ÁLCOOL' : 'GASOLINA';

    setResult({ ratio, recommend, alcVal, gasVal });

    onResultChange([
      { label: 'Preço Álcool', value: `R$ ${alcVal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Preço Gasolina', value: `R$ ${gasVal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Relação', value: `${(ratio * 100).toFixed(1)}%` },
      { label: 'Veredito', value: recommend }
    ]);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header Visual */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-theme-muted-section rounded-full border border-theme-border mb-4">
          <span className="text-2xl">⛽</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">Comparativo de Combustível</span>
        </div>
        <p className="text-theme-muted text-sm">Descubra qual combustível compensa mais para o seu bolso</p>
      </div>

      {/* Cards de Preço */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Card Álcool */}
        <div className={`relative overflow-hidden rounded-3xl border-2 transition-all duration-300 ${result && result.recommend === 'ÁLCOOL' ? 'border-emerald-500 shadow-2xl shadow-emerald-200 scale-105' : 'border-theme-border shadow-lg'}`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${result && result.recommend === 'ÁLCOOL' ? 'from-emerald-500/20 to-emerald-600/10' : 'from-theme-muted-section to-theme-bg'}`} />
          <div className="relative p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${result && result.recommend === 'ÁLCOOL' ? 'bg-emerald-500 text-white shadow-lg' : 'bg-theme-input text-theme-muted'}`}>
                🌱
              </div>
            </div>
            <label className="block text-[11px] font-black text-theme-muted uppercase tracking-wider mb-3">Preço Álcool</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={alc}
                onChange={e => setAlc(maskNumber(e.target.value))}
                className={`w-full text-3xl font-black text-center outline-none bg-transparent ${result && result.recommend === 'ÁLCOOL' ? 'text-emerald-600 dark:text-emerald-400' : 'text-theme-text'}`}
                placeholder="0,00"
              />
            </div>
            <div className="mt-4 text-xs text-theme-muted">por litro</div>
          </div>
        </div>

        {/* Card Gasolina */}
        <div className={`relative overflow-hidden rounded-3xl border-2 transition-all duration-300 ${result && result.recommend === 'GASOLINA' ? 'border-amber-500 shadow-2xl shadow-amber-200 scale-105' : 'border-theme-border shadow-lg'}`}>
          <div className={`absolute inset-0 bg-gradient-to-br ${result && result.recommend === 'GASOLINA' ? 'from-amber-500/20 to-amber-600/10' : 'from-theme-muted-section to-theme-bg'}`} />
          <div className="relative p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl ${result && result.recommend === 'GASOLINA' ? 'bg-amber-500 text-white shadow-lg' : 'bg-theme-input text-theme-muted'}`}>
                ⛽
              </div>
            </div>
            <label className="block text-[11px] font-black text-theme-muted uppercase tracking-wider mb-3">Preço Gasolina</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-3xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={gas}
                onChange={e => setGas(maskNumber(e.target.value))}
                className={`w-full text-4xl font-[1000] text-center outline-none bg-transparent ${result && result.recommend === 'GASOLINA' ? 'text-amber-600 dark:text-amber-400' : 'text-theme-text'}`}
                placeholder="0,00"
              />
            </div>
            <div className="mt-4 text-xs text-theme-muted">por litro</div>
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={calculate}
          className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
        >
          <Icon name="play" className="w-5 h-5" />
          Comparar Combustíveis
        </button>
      </div>

      {/* Resultado Visual */}
      {result && (
        <div className="relative">
          {/* Background Effect */}
          <div className={`absolute inset-0 rounded-[3rem] bg-gradient-to-br ${result.recommend === 'ÁLCOOL' ? 'from-emerald-500 via-emerald-600 to-emerald-700' : 'from-amber-500 via-amber-600 to-amber-700'} opacity-10 blur-3xl`} />

          <div className={`relative p-12 rounded-[3rem] text-center shadow-2xl border-2 transition-all duration-500 ${result.recommend === 'ÁLCOOL' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 border-emerald-400 text-white shadow-emerald-200' : 'bg-gradient-to-br from-amber-500 to-amber-600 border-amber-400 text-white shadow-amber-200'}`}>

            {/* Ícone Grande */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-5xl shadow-xl">
                {result.recommend === 'ÁLCOOL' ? '🌱' : '⛽'}
              </div>
            </div>

            {/* Veredito Principal */}
            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">Veredito Final</p>
              <p className="text-4xl font-black mb-2">VÁ DE {result.recommend}</p>
              <div className="w-24 h-1 bg-theme-result/30 rounded-full mx-auto" />
            </div>

            {/* Informações Detalhadas */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Relação</p>
                <p className="text-2xl font-black">{(result.ratio * 100).toFixed(1)}%</p>
                <p className="text-xs opacity-70 mt-1">álcool ÷ gasolina</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Economia</p>
                <p className="text-2xl font-black">{result.recommend === 'ÁLCOOL' ? '30%' : '0%'}</p>
                <p className="text-xs opacity-70 mt-1">vs. alternativa</p>
              </div>
            </div>

            {/* Regra Prática */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
              <p className="text-xs font-black uppercase opacity-70 mb-2">📏 Regra Prática</p>
              <p className="text-sm font-medium">
                {result.recommend === 'ÁLCOOL'
                  ? 'Álcool compensa quando custa até 70% do preço da gasolina'
                  : 'Gasolina compensa quando o álcool custa mais de 70% do seu preço'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dicas Adicionais */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">💡</span>
            <span className="text-sm font-black text-theme-text">Dica</span>
          </div>
          <p className="text-xs text-theme-muted">Considere o consumo do seu veículo na decisão final</p>
        </div>
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📊</span>
            <span className="text-sm font-black text-theme-text">Análise</span>
          </div>
          <p className="text-xs text-theme-muted">Verifique preços em diferentes postos para melhor comparação</p>
        </div>
        <div className="bg-theme-input p-4 rounded-2xl border border-theme-border">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">🚗</span>
            <span className="text-sm font-black text-theme-text">Performance</span>
          </div>
          <p className="text-xs text-theme-muted">Álcool pode ter menor autonomia por litro</p>
        </div>
      </div>
    </div>
  );
};

export default FuelCalculator;
